import {
  persistWebSurveyOnboardingSession,
  persistWebSurveySurveyFirst,
  readWebSurveyInstrumentId,
  readWebSurveyOnboardingSession,
} from '@/features/auth/lib/constants'
import type { SpInstrumentLinkStartData } from './types'

/** BFP-1131 — pending JWT users (`pending:{sessionId}`) in the public share flow. */

export type GuestSurveyUser = {
  id?: string
  guest?: boolean
  surveyPending?: boolean
} | null | undefined

export function isPendingSurveyUser(user: GuestSurveyUser): boolean {
  if (!user) return false
  if (user.surveyPending === true) return true
  return Boolean(user.id?.startsWith('pending:'))
}

/** Guest or pending share session — still needs the post-survey claim journey. */
export function isGuestSurveyClaimJourney(user: GuestSurveyUser): boolean {
  if (!user) return false
  if (user.guest === true || user.id?.startsWith('guest:')) return true
  return isPendingSurveyUser(user)
}

/** sessionStorage first, then `pending:` / `guest:` JWT user id. */
export function resolvePendingOnboardingSessionId(
  storedSessionId: string | null | undefined,
  userId?: string | null,
): string | null {
  const stored = storedSessionId?.trim() ?? ''
  if (stored) return stored
  if (!userId) return null
  for (const prefix of ['pending:', 'guest:'] as const) {
    if (!userId.startsWith(prefix)) continue
    const id = userId.slice(prefix.length).trim()
    if (id) return id
  }
  return null
}

export function persistSurveyFirstOnboardingHandoff(
  userId?: string | null,
  instrumentId?: string | null,
): string | null {
  const sessionId = resolvePendingOnboardingSessionId(
    readWebSurveyOnboardingSession(),
    userId,
  )
  if (sessionId) persistWebSurveyOnboardingSession(sessionId)
  persistWebSurveySurveyFirst(
    instrumentId?.trim() || readWebSurveyInstrumentId() || undefined,
  )
  return sessionId
}

export function buildSurveyFirstOnboardingSession(
  sessionId: string,
  instrumentId?: string | null,
): SpInstrumentLinkStartData {
  return {
    flow: 'onboarding',
    instrumentId: instrumentId?.trim() || readWebSurveyInstrumentId() || '',
    campaignId: '',
    isVerified: false,
    consent: { privacyRequired: false, termsRequired: false },
    onboardingSessionId: sessionId,
    channels: ['sms'],
    channelAvailability: { email: false, sms: true },
  }
}
